import { S3Upload, useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { useToast } from "@src/lib/hooks/useToast";
import { validateFileType } from "@src/lib/utils/files";
import { useCallback, useState, useEffect, useRef } from "react";

export const useFileListUpload = (
  onFileListUploadSuccess: (mediaFiles: S3Upload[]) => void,
): [(files: File[] | FileList) => void, { loading: boolean }] => {
  const { addErrorToast } = useToast();
  const [validFiles, setValidFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Set<S3Upload>>(new Set());
  const hasCalledSuccessCallback = useRef(false);
  const onFileListUploadSuccessRef = useRef(onFileListUploadSuccess);

  // Keep the ref up to date
  useEffect(() => {
    onFileListUploadSuccessRef.current = onFileListUploadSuccess;
  }, [onFileListUploadSuccess]);

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadComplete: (upload: S3Upload) => {
      setUploadedFiles((prev) => {
        const newSet = new Set(prev);
        newSet.add(upload);
        return newSet;
      });
    },
  });

  useEffect(() => {
    if (
      uploadedFiles.size > 0 &&
      uploadedFiles.size === validFiles.length &&
      !isUploading &&
      !hasCalledSuccessCallback.current
    ) {
      hasCalledSuccessCallback.current = true;
      onFileListUploadSuccessRef.current([...uploadedFiles]);
    }
  }, [uploadedFiles, validFiles, isUploading]);

  const handleFileListEvent = useCallback(
    (files: File[] | FileList) => {
      if (files.length === 0) {
        return;
      }

      const validFiles = [...files].filter(validateFileType);
      if (validFiles.length === 0) {
        addErrorToast({
          message: "Please try again. An invalid file type was uploaded.",
          callsite: "upload_file_list",
        });
        return;
      }
      // Reset the callback flag for new uploads
      hasCalledSuccessCallback.current = false;
      setValidFiles(validFiles);
      setUploadedFiles(new Set()); // Clear previous uploads
      validFiles.forEach((file) => {
        handleFileUpload(file);
      });
    },
    [handleFileUpload, addErrorToast],
  );
  return [handleFileListEvent, { loading: isUploading }];
};
